import React, { useState, useEffect } from 'react';
import { DashboardContentShell, KPIBar, DashboardSection, DashboardWidget } from '../shared/layout/DashboardLayout';
import { MetricCard } from '../shared/layout/CardLayout';
import { useAlertSubscription } from '../shared/hooks/useAlertSubscription';
import { useTelemetrySubscription } from '../shared/hooks/useTelemetrySubscription';
import { useRealTimeData } from '../shared/hooks/useRealTimeData';
import { Alert, Machine, TelemetrySample, UsageProfile, DegradationPattern } from '@cbm/shared';

interface DriftRanking {
  machineId: string;
  machineName: string;
  designVariant: string;
  driftScore: number;
  affectedTags: string[];
  timeToAlert: number; // hours
  severity: 'low' | 'medium' | 'high';
  trend: 'improving' | 'stable' | 'worsening';
}

interface StressIndex {
  profile: UsageProfile;
  pattern: DegradationPattern;
  stressLevel: number;
  machineCount: number;
  averageFailureRate: number;
  criticalComponents: string[];
}

interface VarianceData {
  component: string;
  machines: Array<{
    machineId: string;
    variance: number;
    mean: number;
    coefficient: number;
  }>;
}

type HeatmapPoint = {
  x: string;
  y: string;
  value: number;
  meta?: Record<string, unknown>;
};

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

function formatLabel(v: string) {
  return v
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function HeatmapGrid({
  title,
  subtitle,
  data,
  height = 300,
  valueLabel = 'Value',
}: {
  title: string;
  subtitle?: string;
  data: HeatmapPoint[];
  height?: number;
  valueLabel?: string;
}) {
  const xLabels = Array.from(new Set(data.map((d) => d.x)));
  const yLabels = Array.from(new Set(data.map((d) => d.y)));
  const values = data.map((d) => d.value);
  const minV = values.length ? Math.min(...values) : 0;
  const maxV = values.length ? Math.max(...values) : 1;
  const span = maxV - minV || 1;

  const cellByKey = new Map<string, HeatmapPoint>();
  for (const p of data) cellByKey.set(`${p.x}||${p.y}`, p);

  const cellBg = (v: number) => {
    const t = clamp01((v - minV) / span);
    const hue = 220 - 220 * t;
    return `hsla(${hue}, 85%, 55%, ${0.18 + 0.55 * t})`;
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold text-white">{title}</div>
        {subtitle ? <div className="text-xs text-slate-400">{subtitle}</div> : null}
      </div>

      {data.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
          No heatmap data available.
        </div>
      ) : (
        <div className="overflow-auto rounded-lg border border-slate-700 bg-slate-950/40" style={{ height }}>
          <div
            className="grid"
            style={{
              gridTemplateColumns: `80px repeat(${xLabels.length}, minmax(120px, 1fr))`,
              minWidth: 80 + xLabels.length * 120,
            }}
          >
            <div className="sticky top-0 left-0 z-20 bg-slate-950/80 backdrop-blur px-2 py-2 text-xs font-medium text-slate-300 border-b border-slate-700">
              {valueLabel}
            </div>
            {xLabels.map((x) => (
              <div
                key={`hx-${x}`}
                className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur px-3 py-2 text-xs font-medium text-slate-300 border-b border-slate-700"
                title={formatLabel(x)}
              >
                {formatLabel(x)}
              </div>
            ))}

            {yLabels.map((y) => (
              <React.Fragment key={`row-${y}`}>
                <div className="sticky left-0 z-10 bg-slate-950/80 backdrop-blur px-2 py-2 text-xs font-medium text-slate-300 border-b border-slate-700 border-r border-slate-700 truncate">
                  {formatLabel(y)}
                </div>
                {xLabels.map((x) => {
                  const p = cellByKey.get(`${x}||${y}`);
                  const v = p?.value ?? 0;
                  return (
                    <div
                      key={`cell-${x}-${y}`}
                      className="border-b border-slate-800 border-r border-slate-800 px-3 py-2 relative z-20"
                      style={{ background: cellBg(v) }}
                      title={`${formatLabel(x)} / ${formatLabel(y)}: ${v.toFixed(2)}`}
                    >
                      <div className="text-xs font-semibold text-white">{v.toFixed(2)}</div>
                      {p?.meta ? (
                        <div className="mt-0.5 text-[11px] text-slate-200/80 truncate">
                          {Object.entries(p.meta)
                            .slice(0, 1)
                            .map(([k, val]) => `${k}: ${String(val)}`)
                            .join('')}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-3 text-xs text-slate-300">
        <span className="text-slate-400">Low</span>
        <div className="h-2 flex-1 rounded" style={{
          background:
            'linear-gradient(90deg, hsla(220,85%,55%,0.18), hsla(180,85%,55%,0.45), hsla(120,85%,55%,0.55), hsla(60,85%,55%,0.65), hsla(0,85%,55%,0.73))',
        }} />
        <span className="text-slate-400">High</span>
        <span className="ml-auto text-slate-400">Range: {minV.toFixed(2)} → {maxV.toFixed(2)}</span>
      </div>
    </div>
  );
}

interface DesignWeakness {
  component: string;
  designVariant: string;
  weaknessType: 'material' | 'design' | 'manufacturing' | 'maintenance';
  severity: number;
  affectedMachines: number;
  recommendedAction: string;
  evidence: string[];
}

interface DriftRankingTableProps {
  rankings: DriftRanking[];
  onViewDetails: (machineId: string) => void;
}

function DriftRankingTable({ rankings, onViewDetails }: DriftRankingTableProps) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'text-red-400 bg-red-500/20';
      case 'medium': return 'text-amber-400 bg-amber-500/20';
      case 'low': return 'text-emerald-400 bg-emerald-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'worsening': return '📉';
      case 'stable': return '➡️';
      default: return '➡️';
    }
  };

  return (
    <DashboardSection title="Cross-Machine Drift Ranking" subtitle="Performance degradation analysis">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Machine
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Drift Score
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Affected Tags
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Time to Alert
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Trend
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {rankings.filter(r => r.driftScore !== null && r.machineId.match(/^M-\d{3}$/)).map((ranking, index) => (
              <tr key={ranking.machineId} className="hover:bg-slate-700/50">
                <td className="px-4 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-white">{ranking.machineName}</div>
                    <div className="text-sm text-slate-400">{ranking.designVariant}</div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-white">{ranking.driftScore.toFixed(2)}</div>
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(ranking.severity)}`}>
                      {ranking.severity}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-white">{ranking.affectedTags.length} tags</div>
                  <div className="text-xs text-slate-400">{ranking.affectedTags.slice(0, 2).join(', ')}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                  {ranking.timeToAlert.toFixed(1)}h
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="mr-1">{getTrendIcon(ranking.trend)}</span>
                    <span className="text-sm text-white capitalize">{ranking.trend}</span>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => onViewDetails(ranking.machineId)}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardSection>
  );
}

interface StressIndexVisualizationProps {
  stressIndices: StressIndex[];
}

function StressIndexVisualization({ stressIndices }: StressIndexVisualizationProps) {
  const [selectedProfile, setSelectedProfile] = useState<UsageProfile | null>(null);

  const heatmapData: HeatmapPoint[] = stressIndices.map((index) => ({
    x: formatLabel(String(index.profile)),
    y: formatLabel(String(index.pattern)),
    value: index.stressLevel,
    meta: {
      machines: index.machineCount,
      failureRate: index.averageFailureRate.toFixed(2),
    },
  }));

  const getStressColor = (level: number) => {
    if (level > 80) return 'bg-red-500';
    if (level > 60) return 'bg-amber-500';
    if (level > 40) return 'bg-blue-500';
    return 'bg-emerald-500';
  };

  return (
    <DashboardSection title="Stress Index by Profile & Pattern" subtitle="Usage pattern analysis">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Heatmap */}
        <div>
          <HeatmapGrid
            title="Stress Heatmap"
            subtitle="Higher = more stress (risk)"
            data={heatmapData}
            height={300}
            valueLabel="Stress"
          />
        </div>

        {/* Detailed breakdown */}
        <div className="space-y-3">
          <h4 className="text-md font-medium text-white">Stress Breakdown</h4>
          {stressIndices
            .filter(index => !selectedProfile || index.profile === selectedProfile)
            .sort((a, b) => b.stressLevel - a.stressLevel)
            .map((index, i) => (
              <div 
                key={`${index.profile}-${index.pattern}`}
                className="p-3 bg-slate-800 border border-slate-700 rounded-lg hover:border-slate-600 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${getStressColor(index.stressLevel)}`} />
                    <span className="text-sm font-medium text-white">
                      {index.profile.replace('_', ' ')} / Pattern {index.pattern}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-white">
                    {index.stressLevel.toFixed(1)}%
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                  <div>Machines: {index.machineCount}</div>
                  <div>Failure Rate: {index.averageFailureRate.toFixed(2)}%</div>
                </div>
                
                <div className="mt-2">
                  <div className="text-xs text-slate-400">Critical Components:</div>
                  <div className="text-xs text-slate-300">
                    {index.criticalComponents.join(', ')}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Profile filter */}
      <div className="mt-4 flex items-center space-x-2">
        <span className="text-sm text-slate-400">Filter by profile:</span>
        <select
          value={selectedProfile || ''}
          onChange={(e) => setSelectedProfile(e.target.value as UsageProfile || null)}
          className="text-sm bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white focus:outline-none focus:border-blue-500"
        >
          <option value="">All Profiles</option>
          <option value="normal">Normal</option>
          <option value="aggressive">Aggressive</option>
          <option value="poor_pm">Poor PM</option>
        </select>
      </div>
    </DashboardSection>
  );
}

interface VarianceHeatmapProps {
  varianceData: VarianceData[];
}

function VarianceHeatmap({ varianceData }: VarianceHeatmapProps) {
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);

  const heatmapData: HeatmapPoint[] = varianceData.flatMap((component) =>
    component.machines.map((machine) => ({
      x: formatLabel(component.component),
      y: formatLabel(machine.machineId),
      value: machine.coefficient,
      meta: { variance: machine.variance.toFixed(2), mean: machine.mean.toFixed(2) },
    }))
  );

  const selectedData = selectedComponent 
    ? varianceData.find(c => c.component === selectedComponent)
    : null;

  return (
    <DashboardSection title="Variance Heatmap" subtitle="Component variance analysis">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Component list */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-white">Components</h4>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {varianceData.map(component => (
              <button
                key={component.component}
                onClick={() => setSelectedComponent(
                  selectedComponent === component.component ? null : component.component
                )}
                className={`
                  w-full text-left p-2 rounded text-sm transition-colors
                  ${selectedComponent === component.component
                    ? 'bg-blue-600 text-white border border-blue-500'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  }
                `}
              >
                {component.component}
                <div className="text-xs text-slate-400">
                  {component.machines.length} machines
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Heatmap */}
        <div className="lg:col-span-2">
          <HeatmapGrid
            title="Variance Heatmap"
            subtitle="Coefficient of variation (higher = more unstable)"
            data={heatmapData}
            height={320}
            valueLabel="CoV"
          />
        </div>
      </div>

      {/* Detailed view for selected component */}
      {selectedData && (
        <div className="mt-6 border-t border-slate-700 pt-4">
          <h4 className="text-md font-medium text-white mb-3">
            {selectedData.component} - Machine Breakdown
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {selectedData.machines
              .sort((a, b) => b.coefficient - a.coefficient)
              .map(machine => (
                <div key={machine.machineId} className="p-3 bg-slate-800 border border-slate-700 rounded-lg">
                  <div className="text-sm font-medium text-white">{machine.machineId}</div>
                  <div className="mt-1 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">Coefficient:</span>
                      <span className={`font-medium ${
                        machine.coefficient > 0.3 ? 'text-red-400' :
                        machine.coefficient > 0.2 ? 'text-amber-400' :
                        'text-emerald-400'
                      }`}>
                        {machine.coefficient.toFixed(3)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">Mean:</span>
                      <span className="text-slate-300">{machine.mean.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">Variance:</span>
                      <span className="text-slate-300">{machine.variance.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </DashboardSection>
  );
}

interface DesignWeaknessAnalysisProps {
  weaknesses: DesignWeakness[];
}

function DesignWeaknessAnalysis({ weaknesses }: DesignWeaknessAnalysisProps) {
  const [selectedWeakness, setSelectedWeakness] = useState<DesignWeakness | null>(null);

  const getWeaknessTypeColor = (type: string) => {
    switch (type) {
      case 'material': return 'bg-red-500/20 text-red-400';
      case 'design': return 'bg-blue-500/20 text-blue-400';
      case 'manufacturing': return 'bg-amber-500/20 text-amber-400';
      case 'maintenance': return 'bg-emerald-500/20 text-emerald-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getSeverityColor = (severity: number) => {
    if (severity > 80) return 'text-red-400';
    if (severity > 60) return 'text-amber-400';
    return 'text-blue-400';
  };

  return (
    <DashboardSection title="Design Weakness Identification" subtitle="Component failure analysis">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weakness list */}
        <div className="space-y-3">
          <h4 className="text-md font-medium text-white">Identified Weaknesses</h4>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {weaknesses
              .sort((a, b) => b.severity - a.severity)
              .map((weakness, index) => (
                <div
                  key={index}
                  className={`
                    p-3 border rounded-lg cursor-pointer transition-colors
                    ${selectedWeakness === weakness
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 hover:border-slate-600 bg-slate-800'
                    }
                  `}
                  onClick={() => setSelectedWeakness(
                    selectedWeakness === weakness ? null : weakness
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-white">{weakness.component}</div>
                    <div className={`text-sm font-bold ${getSeverityColor(weakness.severity)}`}>
                      {weakness.severity.toFixed(0)}%
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getWeaknessTypeColor(weakness.weaknessType)}`}>
                      {weakness.weaknessType}
                    </span>
                    <span className="text-sm text-slate-400">
                      {weakness.affectedMachines} machines
                    </span>
                  </div>
                  
                  <div className="text-sm text-slate-400">{weakness.designVariant}</div>
                </div>
              ))}
          </div>
        </div>

        {/* Detailed view */}
        <div>
          {selectedWeakness ? (
            <div className="space-y-4">
              <div>
                <h4 className="text-md font-medium text-white mb-2">
                  {selectedWeakness.component} Analysis
                </h4>
                <div className="bg-slate-800 border border-slate-700 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-slate-400">Design Variant:</div>
                      <div className="text-sm font-medium text-white">
                        {selectedWeakness.designVariant}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-400">Weakness Type:</div>
                      <div className="text-sm font-medium text-white capitalize">
                        {selectedWeakness.weaknessType}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-400">Severity Score:</div>
                      <div className={`text-sm font-bold ${getSeverityColor(selectedWeakness.severity)}`}>
                        {selectedWeakness.severity.toFixed(0)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-400">Affected Machines:</div>
                      <div className="text-sm font-medium text-white">
                        {selectedWeakness.affectedMachines}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-sm font-medium text-white mb-2">Recommended Action</h5>
                <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg">
                  <div className="text-sm text-blue-300">
                    {selectedWeakness.recommendedAction}
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-sm font-medium text-white mb-2">Supporting Evidence</h5>
                <div className="space-y-1">
                  {selectedWeakness.evidence.map((evidence, i) => (
                    <div key={i} className="text-sm text-slate-400 flex items-start">
                      <span className="mr-2">•</span>
                      <span>{evidence}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-500">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-slate-700 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-2xl">🔍</span>
                </div>
                <p className="text-sm">Select a weakness to view detailed analysis</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardSection>
  );
}

// Statistical calculation helpers
function calculateVariance(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
  return squaredDiffs.reduce((sum, v) => sum + v, 0) / values.length;
}

function calculateMean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function calculateCoefficientOfVariation(values: number[]): number {
  const mean = calculateMean(values);
  if (mean === 0) return 0;
  const stdDev = Math.sqrt(calculateVariance(values));
  return stdDev / mean;
}

export function OEMEngineeringDashboard() {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [driftRankings, setDriftRankings] = useState<DriftRanking[]>([]);
  const [stressIndices, setStressIndices] = useState<StressIndex[]>([]);
  const [varianceData, setVarianceData] = useState<VarianceData[]>([]);
  const [designWeaknesses, setDesignWeaknesses] = useState<DesignWeakness[]>([]);
  const [selectedMachine, setSelectedMachine] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [telemetryHistory, setTelemetryHistory] = useState<Map<string, TelemetrySample[]>>(new Map());
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Real-time data subscriptions
  const { alerts, isLoading: alertsLoading } = useAlertSubscription();
  const { samples, subscribe } = useTelemetrySubscription();
  const { isConnected } = useRealTimeData(null as any, () => true as any, () => true as any, () => true as any);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch real analytics from API
        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/analytics`);
        
        if (response.ok) {
          const analyticsData = await response.json();
          
          console.log('OEM Engineering: Analytics API response:', analyticsData);
          
          // Set drift rankings from API
          if (analyticsData.driftRankings) {
            console.log('OEM Engineering: Setting drift rankings:', analyticsData.driftRankings.length);
            setDriftRankings(analyticsData.driftRankings);
          }
          
          // Set design weaknesses from API
          if (analyticsData.designWeaknesses) {
            console.log('OEM Engineering: Setting design weaknesses:', analyticsData.designWeaknesses);
            setDesignWeaknesses(analyticsData.designWeaknesses);
          }
        } else {
          console.error('OEM Engineering: Analytics API failed:', response.status);
        }

        // Fetch machines
        const mockMachines: Machine[] = [
          {
            pk: 'TENANT#demo',
            sk: 'MACHINE#machine-001',
            displayName: 'Production Line A',
            designVariant: 'Model X1',
            usageProfile: 'normal',
            degradationPattern: 'A',
            createdAt: new Date().toISOString(),
          } as any,
          {
            pk: 'TENANT#demo',
            sk: 'MACHINE#machine-002',
            displayName: 'Production Line B',
            designVariant: 'Model X2',
            usageProfile: 'aggressive',
            degradationPattern: 'B',
            createdAt: new Date().toISOString(),
          } as any,
          {
            pk: 'TENANT#demo',
            sk: 'MACHINE#machine-003',
            displayName: 'Quality Control Station',
            designVariant: 'Model Q1',
            usageProfile: 'poor_pm',
            degradationPattern: 'C',
            createdAt: new Date().toISOString(),
          } as any
        ];

        // Mock stress indices (still calculated from telemetry)
        const mockStressIndices: StressIndex[] = [
          {
            profile: 'aggressive',
            pattern: 'A',
            stressLevel: 87.3,
            machineCount: 5,
            averageFailureRate: 12.4,
            criticalComponents: ['bearing', 'motor', 'coupling']
          },
          {
            profile: 'poor_pm',
            pattern: 'B',
            stressLevel: 78.9,
            machineCount: 3,
            averageFailureRate: 9.8,
            criticalComponents: ['filter', 'valve', 'sensor']
          },
          {
            profile: 'normal',
            pattern: 'C',
            stressLevel: 45.2,
            machineCount: 8,
            averageFailureRate: 4.1,
            criticalComponents: ['seal', 'gasket']
          }
        ];

        // Mock variance data (still calculated from telemetry)
        const mockVarianceData: VarianceData[] = [
          {
            component: 'vibration',
            machines: [
              { machineId: 'M-001', variance: 0.45, mean: 2.3, coefficient: 0.29 },
              { machineId: 'M-002', variance: 1.23, mean: 3.8, coefficient: 0.29 },
              { machineId: 'M-003', variance: 0.12, mean: 1.8, coefficient: 0.19 }
            ]
          },
          {
            component: 'temperature',
            machines: [
              { machineId: 'M-001', variance: 12.4, mean: 75.2, coefficient: 0.15 },
              { machineId: 'M-002', variance: 28.7, mean: 82.1, coefficient: 0.21 },
              { machineId: 'M-003', variance: 8.9, mean: 72.3, coefficient: 0.13 }
            ]
          }
        ];

        setMachines(mockMachines);
        setStressIndices(mockStressIndices);
        setVarianceData(mockVarianceData);
        
        // Subscribe to all machines for telemetry
        mockMachines.forEach(machine => {
          const machineId = machine.sk.split('#')[1];
          subscribe(machineId);
          console.log('OEM Engineering: Subscribing to machine:', machineId);
        });
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [subscribe]);

  // Process incoming telemetry samples for live heatmap updates
  useEffect(() => {
    if (samples.length === 0) return;

    // Update telemetry history and calculate heatmaps
    setTelemetryHistory(prev => {
      const updated = new Map(prev);
      samples.forEach(sample => {
        const machineId = sample.machineId;
        const history = updated.get(machineId) || [];
        history.push(sample);
        updated.set(machineId, history.slice(-100));
      });

      // Calculate variance data from UPDATED history
      const componentNames = ['cycle_time', 'temperature', 'vibration_level', 'pressure', 'power_consumption'];
      const newVarianceData: VarianceData[] = componentNames.map(component => {
        const machineVariances = Array.from(updated.entries()).map(([machineId, history]) => {
          const values = history
            .map(s => s.tags.find(t => t.name === component)?.value)
            .filter((v): v is number => v !== undefined);
          
          if (values.length < 5) return null;

          const mean = calculateMean(values);
          const variance = calculateVariance(values);
          const coefficient = calculateCoefficientOfVariation(values);

          return { machineId, variance, mean, coefficient };
        }).filter((v): v is NonNullable<typeof v> => v !== null);

        return { component, machines: machineVariances };
      }).filter(v => v.machines.length > 0);

      if (newVarianceData.length > 0) {
        setVarianceData(newVarianceData);
      }

      // Calculate stress indices from UPDATED history
      const profilePatternMap = new Map<string, { samples: TelemetrySample[], machines: Set<string> }>();
      
      machines.forEach(machine => {
        const machineId = machine.sk.split('#')[1];
        const history = updated.get(machineId) || [];
        if (history.length === 0) return;

        const degradationPattern = (machine as any).degradationPattern;
        const key = `${machine.usageProfile || 'normal'}-${degradationPattern || 'none'}`;
        if (!profilePatternMap.has(key)) {
          profilePatternMap.set(key, { samples: [], machines: new Set() });
        }
        const entry = profilePatternMap.get(key)!;
        entry.samples.push(...history);
        entry.machines.add(machineId);
      });

      const newStressIndices: StressIndex[] = [];
      profilePatternMap.forEach((data, key) => {
        const [profile, pattern] = key.split('-');
        if (!pattern || pattern === 'none' || !data.samples.length) return;

        const vibrationValues = data.samples
          .map(s => s.tags.find(t => t.name === 'vibration_level')?.value)
          .filter((v): v is number => v !== undefined);
        const tempValues = data.samples
          .map(s => s.tags.find(t => t.name === 'temperature')?.value)
          .filter((v): v is number => v !== undefined);

        const vibrationCV = calculateCoefficientOfVariation(vibrationValues);
        const tempCV = calculateCoefficientOfVariation(tempValues);
        const stressLevel = Math.min(100, (vibrationCV + tempCV) * 100);

        const machineAlerts = alerts.filter(a => 
          data.machines.has(a.machineId) && (a.severity === 'MID' || a.severity === 'LATE')
        );
        const failureRate = (machineAlerts.length / data.machines.size) * 10;

        newStressIndices.push({
          profile: profile as UsageProfile,
          pattern: pattern as DegradationPattern,
          stressLevel,
          machineCount: data.machines.size,
          averageFailureRate: failureRate,
          criticalComponents: vibrationCV > 0.3 ? ['vibration', 'bearing'] : tempCV > 0.2 ? ['temperature', 'cooling'] : ['general']
        });
      });

      if (newStressIndices.length > 0) {
        setStressIndices(newStressIndices);
      }

      console.log('OEM Engineering: Updated heatmaps', {
        varianceComponents: newVarianceData.length,
        stressIndices: newStressIndices.length,
        totalSamples: samples.length,
        historySize: updated.size,
        sampleCounts: Array.from(updated.entries()).map(([id, hist]) => `${id}:${hist.length}`).join(', ')
      });

      // Force re-render
      setUpdateTrigger(prev => prev + 1);

      return updated;
    });
  }, [samples, machines, alerts]);

  const handleViewMachineDetails = (machineId: string) => {
    setSelectedMachine(selectedMachine === machineId ? null : machineId);
  };

  // Filter alerts for OEM engineering (all alerts for analysis)
  const engineeringAlerts = alerts;

  if (loading) {
    return (
      <DashboardContentShell>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </DashboardContentShell>
    );
  }

  return (
    <DashboardContentShell>
      <KPIBar
        metrics={[
          { label: 'Machines', value: machines.length, status: 'good' },
          { label: 'Drift Alerts', value: driftRankings.filter(r => r.severity === 'high').length, status: driftRankings.filter(r => r.severity === 'high').length > 0 ? 'critical' : 'good' },
          { label: 'Stress Level', value: `${(stressIndices.reduce((sum, s) => sum + s.stressLevel, 0) / stressIndices.length || 0).toFixed(1)}%`, status: 'warning' },
          { label: 'Weaknesses', value: designWeaknesses.length, status: designWeaknesses.filter(w => w.severity > 80).length > 0 ? 'critical' : 'good' },
          { label: 'Status', value: isConnected ? 'LIVE' : 'OFFLINE', status: isConnected ? 'good' : 'critical' },
        ]}
      />

      {/* Cross-Machine Drift Ranking */}
      <DriftRankingTable
        rankings={driftRankings}
        onViewDetails={handleViewMachineDetails}
      />

      {/* Machine Detail View */}
      {selectedMachine && (
        <DashboardSection 
          title={`Machine Details: ${driftRankings.find(r => r.machineId === selectedMachine)?.machineName || selectedMachine}`}
          subtitle="Detailed drift analysis and telemetry trends"
        >
          <div className="space-y-4">
            {(() => {
              const ranking = driftRankings.find(r => r.machineId === selectedMachine);
              if (!ranking) return <p className="text-slate-400">Machine not found</p>;
              
              return (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard title="Drift Score" value={ranking.driftScore.toFixed(2)} />
                    <MetricCard title="Time to Alert" value={`${ranking.timeToAlert.toFixed(1)}h`} />
                    <MetricCard title="Severity" value={ranking.severity.toUpperCase()} />
                    <MetricCard title="Trend" value={ranking.trend.toUpperCase()} />
                  </div>

                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Affected Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {ranking.affectedTags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Analysis Summary</h4>
                    <div className="space-y-2 text-sm text-slate-300">
                      <p>• Design Variant: <span className="text-white font-medium">{ranking.designVariant}</span></p>
                      <p>• Drift Score indicates {ranking.severity === 'high' ? 'significant' : ranking.severity === 'medium' ? 'moderate' : 'minor'} deviation from baseline performance</p>
                      <p>• Performance trend is {ranking.trend === 'worsening' ? 'deteriorating' : ranking.trend === 'improving' ? 'improving' : 'stable'}</p>
                      <p>• Average time to alert generation: {ranking.timeToAlert.toFixed(1)} hours</p>
                    </div>
                  </div>

                  <button
                    onClick={() => setSelectedMachine(null)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Close Details
                  </button>
                </>
              );
            })()}
          </div>
        </DashboardSection>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Stress Index Visualization */}
        <StressIndexVisualization stressIndices={stressIndices} />

        {/* Design Weakness Analysis */}
        <DesignWeaknessAnalysis weaknesses={designWeaknesses} />
      </div>

      {/* Variance Heatmap */}
      <VarianceHeatmap varianceData={varianceData} />
    </DashboardContentShell>
  );
}